import { useCallback, useSyncExternalStore } from "react";
const CHANGE_EVENT = "amoreg:inbox-local-change";
const memory = new Map<string, string>();

function snapshot(scope: string | null): string {
  if (!scope || typeof window === "undefined") return "";
  // Retain a failed write for this session, so a quota error doesn't undo the action.
  if (memory.has(scope)) return memory.get(scope)!;
  try {
    return window.localStorage.getItem(scope) ?? "";
  } catch {
    return "";
  }
}

/** Local read receipts shared by open tabs; no document or invitation is mutated. */
export function useInboxLocalState(scope: string | null) {
  const subscribe = useCallback(
    (notify: () => void) => {
      const onChange = (event: Event) => {
        if (event instanceof StorageEvent) {
          if (event.key !== scope && event.key !== null) return;
          if (scope) memory.delete(scope);
        } else if ((event as CustomEvent<string>).detail !== scope) return;
        notify();
      };
      window.addEventListener("storage", onChange);
      window.addEventListener(CHANGE_EVENT, onChange);
      return () => {
        window.removeEventListener("storage", onChange);
        window.removeEventListener(CHANGE_EVENT, onChange);
      };
    },
    [scope],
  );
  const getSnapshot = useCallback(() => snapshot(scope), [scope]);
  const raw = useSyncExternalStore(subscribe, getSnapshot, () => "");
  const update = (change: (raw: string) => string): boolean => {
    if (!scope) return false;
    const next = change(snapshot(scope));
    let persisted = true;
    try {
      window.localStorage.setItem(scope, next);
      memory.delete(scope);
    } catch {
      memory.set(scope, next);
      persisted = false;
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: scope }));
    return persisted;
  };
  return { raw, update, available: scope !== null };
}
